<template>
  <v-card class="elevation-10 document-card mb-6">
    <div class="specific-people-info-container">
      <div class="title-row d-flex align-center">
        <h2 class="people-headings flex-grow-1 pr-3 mb-0">{{ titleText }}</h2>
      </div>

      <div v-if="hasFiles" class="files mt-4">
        <DocumentFileRow
          v-for="file in document.files"
          :key="file.id"
          :file="file"
          :read-only="!canDeleteDocuments"
          @file-deleted="$emit('refetch')"
        />
      </div>

      <div v-if="hasFiles && document.notes" class="document-meta mt-3">
        <p class="document-meta-row">
          <strong>Notes:</strong> {{ document.notes }}
        </p>
      </div>

      <div
        v-if="!hasFiles"
        class="dropzone d-flex align-center mt-4"
        data-test="open-upload"
        @click="showAddFiles = true"
      >
        <DocumentIconBadge icon="upload" tile class="mr-3" />
        <div class="flex-grow-1">
          <div class="dropzone-title">Click to upload</div>
          <div class="dropzone-hint">PDF, JPG or PNG · Up to 30 MB</div>
        </div>
        <DocumentUploadButton @click="showAddFiles = true" />
      </div>

      <div v-if="hasFiles" class="card-button-container no-border">
        <FunkButton
          button-text="Edit title or note"
          @buttonClicked="showEdit = true"
        />
      </div>

      <div v-if="hasFiles || canDeleteDocuments" class="card-cta-row">
        <DocumentDeleteCardButton
          v-if="canDeleteDocuments"
          :class="{ 'mr-auto': hasFiles }"
          aria-label="Delete document"
          data-test="delete-document"
          @click="showDelete = true"
        />
        <DocumentUploadButton
          v-if="hasFiles"
          label="Upload another"
          @click="showAddFiles = true"
        />
      </div>

      <UploadDocumentDialog
        v-if="showAddFiles"
        v-model="showAddFiles"
        :viewed-testator-id="viewedTestatorId"
        :document-type="category.type"
        :document-type-display-name="titleText"
        :description="''"
        :allow-multiple-files="true"
        :existing-document-id="document.id"
        @uploaded="onUploaded"
      />

      <EditDocumentDialog
        v-if="showEdit"
        v-model="showEdit"
        :document="document"
        :requires-title="true"
        @saved="onEdited"
      />

      <DeleteDocumentDialog
        v-if="showDelete"
        v-model="showDelete"
        :document="documentForDelete"
        @deleted="onDeleted"
      />
    </div>
  </v-card>
</template>

<script>
  import DocumentFileRow from './DocumentFileRow.vue';
  import DocumentIconBadge from './DocumentIconBadge.vue';
  import DocumentDeleteCardButton from './DocumentDeleteCardButton.vue';
  import UploadDocumentDialog from './UploadDocumentDialog.vue';
  import EditDocumentDialog from './EditDocumentDialog.vue';
  import DeleteDocumentDialog from './DeleteDocumentDialog.vue';
  import DocumentUploadButton from './DocumentUploadButton.vue';
  import FunkButton from '../guest/FunkButton.vue';
  import { documentDeletePermissionMixin } from '../../mixins/document-delete-permission';

  export default {
    name: 'OtherDocumentCard',
    components: {
      DocumentFileRow,
      DocumentIconBadge,
      DocumentDeleteCardButton,
      UploadDocumentDialog,
      EditDocumentDialog,
      DeleteDocumentDialog,
      DocumentUploadButton,
      FunkButton,
    },
    mixins: [documentDeletePermissionMixin],
    props: {
      viewedTestatorId: { type: [Number, String], required: true },
      category: { type: Object, required: true },
      document: { type: Object, required: true },
    },
    data() {
      return {
        showAddFiles: false,
        showEdit: false,
        showDelete: false,
      };
    },
    computed: {
      titleText() {
        return this.document.name || 'Untitled document';
      },
      hasFiles() {
        return (this.document.files || []).length > 0;
      },
      documentForDelete() {
        return {
          ...this.document,
          displayName: this.titleText,
          fileCount: (this.document.files || []).length,
        };
      },
    },
    methods: {
      onUploaded() {
        this.$emit('refetch');
      },
      onEdited() {
        this.$emit('refetch');
      },
      onDeleted({ id }) {
        this.$emit('deleted', { id, displayName: this.titleText });
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '../../constants/styles/people-overview-info-blocks.scss';
  @import '../../constants/styles/document-card.scss';
</style>
